import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import {
  getArtifact,
  type ArtifactDefinition,
  type ArtifactId,
  type ViewMode,
} from '@/artifacts';
import { useGeoMarkupStore } from '@/store';
import { VIEW_MODE } from '@/types';

import {
  resolveBackgroundArtifact,
  useBackgroundLocation,
  type BackgroundLocationState,
} from './useBackgroundLocation';

/**
 * Parses the `?view=` query value against an artifact's supported set.
 * Falls back to the artifact's `defaultView` when the value is absent
 * or not supported (e.g. `?view=kanban` on the notes artifact).
 */
function parseView(raw: string | null, artifact: ArtifactDefinition): ViewMode {
  if (
    raw !== null &&
    (artifact.supportedViews as readonly ViewMode[]).includes(raw as ViewMode)
  ) {
    return raw as ViewMode;
  }
  return artifact.defaultView;
}

/**
 * Reads the active map view mode from the `?view=` query param.
 *
 * URL is the source of truth. The Zustand store still keeps a per-artifact
 * mirror that is now a **private implementation detail** of the settings
 * persistence layer (see `settingsBridge`): this hook fires the artifact's
 * `syncViewMode` side-effect whenever the URL changes, so the mirror stays
 * up to date without any React component reading it directly.
 *
 * The returned `view` is the effective mode for the **currently active
 * background artifact** (tasks or notes). `setView` writes the new mode
 * into the URL (replacing history so view-toggle does not pollute
 * back-button history).
 */
export function useMapViewMode(): {
  view: ViewMode;
  setView: (mode: string) => void;
  artifact: ArtifactId;
} {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const backgroundLocation = useBackgroundLocation();

  const artifactId = resolveBackgroundArtifact(backgroundLocation.pathname);
  // `getArtifact` returns `null` only for ids outside the registry; since
  // `resolveBackgroundArtifact` already constrains to `ArtifactId`, the
  // lookup is effectively total. Fall back to the first registered
  // artifact defensively so a missing entry cannot break the hook.
  const artifact = getArtifact(artifactId);

  const raw = searchParams.get('view');
  const view: ViewMode = artifact
    ? parseView(raw, artifact)
    : // Defensive default — registry is static so this branch should
      // never execute at runtime.
      VIEW_MODE.MAP;

  // Mirror URL → persistence store via the artifact registry. Each
  // artifact owns its own `syncViewMode` side-effect (see `tasks.ts` /
  // `notes.ts`), keeping the mirror as a private implementation detail of
  // the settings-persistence layer. Artifacts that don't need persistence
  // simply omit `syncViewMode` and this effect becomes a no-op.
  useEffect(() => {
    getArtifact(artifactId)?.syncViewMode?.(view, useGeoMarkupStore.getState());
  }, [artifactId, view]);

  const setView = useCallback(
    (mode: string) => {
      const defaultView = artifact?.defaultView ?? VIEW_MODE.MAP;
      const next = new URLSearchParams(location.search);
      if (mode === defaultView) {
        next.delete('view');
      } else {
        next.set('view', mode);
      }
      const search = next.toString();
      const newSearch = search ? `?${search}` : '';

      // Also patch backgroundLocation.search so the background page
      // (inside MapBackgroundRoutes) sees the new view immediately,
      // not only after closeOverlay().
      const currentState = location.state as BackgroundLocationState | null;
      const bgLoc = currentState?.backgroundLocation;
      const newState = bgLoc
        ? {
            ...currentState,
            backgroundLocation: { ...bgLoc, search: newSearch },
          }
        : location.state;

      // Use navigate (not setSearchParams) so we can explicitly preserve
      // `location.state` — notably `backgroundLocation`. setSearchParams
      // would wipe state, causing closeOverlay to lose the background and
      // fall back to `/map/tasks` (view=map), which looked like a reload
      // back to the map on form close.
      navigate(
        { pathname: location.pathname, search: newSearch },
        { replace: true, state: newState }
      );
    },
    [
      navigate,
      location.pathname,
      location.search,
      location.state,
      artifact?.defaultView,
    ]
  );

  return { view, setView, artifact: artifactId };
}
